import React, { useCallback, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import { cn } from '../../../../../lib/utils';
import randomId from '../../../../../utils/randomId';
import SkillDetailForm from './SkillDetailForm';

function SkillTreeNode({
  node,
  treeData,
  updateTreeNode,
  addTreeNode,
  removeTreeNode,
  growLevels,
  expanded,
  toggleExpand,
  isDetailActive,
  setIsDetailActive,
  depth = 0,
}) {
  const [detailsAmount, setDetailsAmount] = useState(node.details.length);
  const inputRef = useRef(null);
  const isExpanded = expanded.includes(node.itemId);
  const nodeChildren = treeData.filter((item) => node.children.includes(item.itemId));
  const hasChildren = node.children.length > 0;

  const handleNodeClick = () => {
    if (isExpanded) {
      // Already expanded - focus the input for editing
      inputRef.current?.focus();
      inputRef.current?.select();
    } else {
      // Not expanded - expand it
      toggleExpand(node.itemId);
    }
  };

  const listenAddNewDetails = useCallback(() => {
    const itemId = randomId();
    updateTreeNode(node.itemId, {
      details: [
        ...node.details,
        {
          itemId,
          name: '',
          successCriteria: '',
          materialName: '',
          materialType: '',
          link: '',
          level: growLevels[0]?._id,
        },
      ],
    });

    setTimeout(() => {
      const element = document.getElementById(itemId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    });
  }, [node, updateTreeNode, growLevels]);

  const listenRemoveDetails = useCallback(
    (data) => {
      updateTreeNode(node.itemId, {
        details: node.details.filter((d) => d.itemId !== data.itemId),
      });
    },
    [node, updateTreeNode],
  );

  const listenUpdateDetails = useCallback(
    (data) => {
      const newDetails = node.details.map((d) => (d.itemId === data.itemId ? data : d));
      if (node.details.findIndex((detail) => detail.itemId === data.itemId) === -1) {
        newDetails.push(data);
      }
      updateTreeNode(node.itemId, {
        details: newDetails,
      });
    },
    [node, updateTreeNode],
  );

  return (
    <div
      className={cn('border rounded-lg bg-card', depth > 0 && 'ml-6')}
      style={{ borderColor: 'hsl(var(--border))' }}
    >
      {/* Node Header */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className="flex items-center gap-2 cursor-pointer" onClick={handleNodeClick}>
        {/* Expand/Collapse Button */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toggleExpand(node.itemId);
          }}
          className={cn(
            'p-1 rounded hover:bg-muted transition-colors',
            !hasChildren && node.details.length === 0 && 'invisible',
          )}
        >
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </button>

        {/* Skill Name Input */}
        <input
          ref={inputRef}
          type="text"
          value={node.name}
          placeholder="Skill name..."
          readOnly={!isExpanded}
          onClick={(e) => {
            e.stopPropagation();
            if (!isExpanded) {
              toggleExpand(node.itemId);
            }
          }}
          onChange={(e) => updateTreeNode(node.itemId, { name: e.target.value })}
          className={cn(
            'flex-1 px-3 py-1.5 rounded-md text-sm',
            'bg-background border border-input',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent',
            'placeholder:text-muted-foreground',
            !isExpanded && 'cursor-pointer',
          )}
        />

        {/* Actions */}
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => addTreeNode(node.itemId)}
            title="Add child skill"
          >
            <Plus className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => removeTreeNode(node.itemId)}
            title="Remove skill"
            className="text-destructive hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Children or Details */}
      {isExpanded && (
        <div className="border-t" style={{ borderColor: 'hsl(var(--border))' }}>
          {hasChildren ? (
            <div className="space-y-2">
              {nodeChildren.map((child) => (
                <SkillTreeNode
                  key={child.itemId}
                  node={child}
                  treeData={treeData}
                  updateTreeNode={updateTreeNode}
                  addTreeNode={addTreeNode}
                  removeTreeNode={removeTreeNode}
                  growLevels={growLevels}
                  expanded={expanded}
                  toggleExpand={toggleExpand}
                  isDetailActive={isDetailActive}
                  setIsDetailActive={setIsDetailActive}
                  depth={depth + 1}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {node.details.map((item, index) => (
                <SkillDetailForm
                  key={item.itemId}
                  detail={item}
                  updateDetails={listenUpdateDetails}
                  removeDetails={listenRemoveDetails}
                  addNewDetails={listenAddNewDetails}
                  isLastItem={index === node.details.length - 1}
                  growLevels={growLevels}
                  nodeItemId={node.itemId}
                  isDetailActive={isDetailActive}
                  setIsDetailActive={setIsDetailActive}
                  detailsAmount={detailsAmount}
                  setDetailsAmount={setDetailsAmount}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const treeNodeShape = {
  itemId: PropTypes.string,
  name: PropTypes.string,
  details: PropTypes.arrayOf(
    PropTypes.shape({
      itemId: PropTypes.string,
      successCriteria: PropTypes.string,
      materialName: PropTypes.string,
      materialType: PropTypes.string,
      link: PropTypes.string,
      level: PropTypes.string,
    }),
  ),
  children: PropTypes.arrayOf(PropTypes.string).isRequired,
};

SkillTreeNode.propTypes = {
  node: PropTypes.shape(treeNodeShape).isRequired,
  treeData: PropTypes.arrayOf(PropTypes.shape(treeNodeShape)).isRequired,
  updateTreeNode: PropTypes.func.isRequired,
  addTreeNode: PropTypes.func.isRequired,
  removeTreeNode: PropTypes.func.isRequired,
  growLevels: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      key: PropTypes.string.isRequired,
      _id: PropTypes.string.isRequired,
    }),
  ).isRequired,
  expanded: PropTypes.arrayOf(PropTypes.string).isRequired,
  toggleExpand: PropTypes.func.isRequired,
  isDetailActive: PropTypes.bool.isRequired,
  setIsDetailActive: PropTypes.func.isRequired,
  depth: PropTypes.number,
};

SkillTreeNode.defaultProps = {
  depth: 0,
};

export default SkillTreeNode;
